import type {IntlShape} from 'react-intl';
import {createToast} from '../toasts/another-toast/toaster';
import {type EditorSurface, trackEditorSaveFailed} from '../../../../tracking/amplitude/editorEvents';

/**
 * The length a company character limit is measured against: the plain text of stored DraftJS
 * content, every block's text joined with nothing between them.
 *
 * That is DraftJS's own `getPlainText('')`, the measure the previous task-modal editor enforced,
 * so a text that fitted there fits here. Formatting, mentions and inline media add nothing.
 * Content that is not DraftJS JSON is measured as it stands.
 */
export const plainTextLength = (content: string | null | undefined): number => {
	if (!content) return 0;
	try {
		const parsed = JSON.parse(content);
		if (Array.isArray(parsed?.blocks)) {
			return parsed.blocks.reduce(
				(sum: number, block: {text?: unknown}) => sum + (typeof block?.text === 'string' ? block.text.length : 0),
				0
			);
		}
	} catch {
		// Not JSON: measured as plain text below.
	}
	return content.length;
};

/**
 * Whether content may be saved under the company's character limit, telling the user and
 * returning false when it may not.
 *
 * A limit of zero or less, or none at all, means unlimited — the default for a company with no
 * limit configured is -1. Returning false rather than throwing lets the caller keep the text
 * in the editor so it can be shortened, which is the only useful thing left to do.
 */
export const withinCharacterLimitOrNotify = (
	content: string | null | undefined,
	limit: number | null | undefined,
	intl: IntlShape,
	surface: EditorSurface
): boolean => {
	if (!limit || limit <= 0) return true;
	const length = plainTextLength(content);
	if (length <= limit) return true;

	createToast({
		duration: 8000,
		message: intl.formatMessage({id: 'rich_text_editor.character_limit_exceeded'}, {length, limit}),
	});
	trackEditorSaveFailed(surface, 'character_limit');
	return false;
};
